import React, { useState, useEffect } from 'react';
import { PlusCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper parsers for template placeholders
const getHeaderPlaceholders = (template) => {
  if (!template) return [];
  const placeholders = [];
  const headerComp = template.components?.find(c => c.type?.toUpperCase() === 'HEADER');
  if (headerComp && headerComp.text) {
    const headerMatches = [...headerComp.text.matchAll(/\{\{([^}]+)\}\}/g)];
    headerMatches.forEach(m => placeholders.push(m[1]));
  }
  return [...new Set(placeholders)].sort((a, b) => {
    const numA = parseInt(a);
    const numB = parseInt(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });
};

const getBodyPlaceholders = (template) => {
  if (!template) return [];
  const placeholders = [];
  const bodyComp = template.components?.find(c => c.type?.toUpperCase() === 'BODY');
  if (bodyComp && bodyComp.text) {
    const bodyMatches = [...bodyComp.text.matchAll(/\{\{([^}]+)\}\}/g)];
    bodyMatches.forEach(m => placeholders.push(m[1]));
  }
  return [...new Set(placeholders)].sort((a, b) => {
    const numA = parseInt(a);
    const numB = parseInt(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });
};

const getTemplateButtonPlaceholders = (template) => {
  if (!template) return [];
  const buttonsComp = template.components?.find(c => c.type?.toUpperCase() === 'BUTTONS');
  if (!buttonsComp || !buttonsComp.buttons) return [];

  const result = [];
  buttonsComp.buttons.forEach((btn, index) => {
    if (btn.type?.toUpperCase() === 'URL' && btn.url && btn.url.includes('{{1}}')) {
      result.push({
        index: index.toString(),
        text: btn.text,
        url: btn.url
      });
    }
  });
  return result;
};

const NewChatModal = ({ isOpen, onClose, initialPhone, onSuccess }) => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatSelectedTemplate, setNewChatSelectedTemplate] = useState(null);
  const [newChatHeaderVariables, setNewChatHeaderVariables] = useState({});
  const [newChatHeaderMediaUrl, setNewChatHeaderMediaUrl] = useState('');
  const [newChatBodyVariables, setNewChatBodyVariables] = useState({});
  const [newChatButtonVariables, setNewChatButtonVariables] = useState({});
  const [sendingTemplate, setSendingTemplate] = useState(false);
  
  // Option to just send a text message to a new number
  const [sendType, setSendType] = useState('template'); // 'text' or 'template'
  const [textMessage, setTextMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNewChatPhone(initialPhone || '');
      setNewChatSelectedTemplate(null);
      setNewChatHeaderVariables({});
      setNewChatHeaderMediaUrl('');
      setNewChatBodyVariables({});
      setNewChatButtonVariables({});
      setTextMessage('');
      setSendType('template');
      fetchTemplates();
    }
  }, [isOpen, initialPhone]);

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSendText = async (e) => {
    e.preventDefault();
    if (!newChatPhone.trim() || !textMessage.trim() || sendingTemplate) return;
    
    try {
      setSendingTemplate(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ to: newChatPhone.trim(), message: textMessage.trim() })
      });
      
      if (res.ok) {
        alert('Message sent successfully!');
        onSuccess(newChatPhone.trim());
      } else {
        const data = await res.json();
        alert(data.message || 'Failed to send message.');
      }
    } catch (err) {
      console.error(err);
      alert('Error sending message.');
    } finally {
      setSendingTemplate(false);
    }
  };

  const handleSendTemplate = async (e) => {
    e.preventDefault();
    if (!newChatPhone || !newChatSelectedTemplate || sendingTemplate) return;

    const bodyComp = newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'BODY');
    const templateBodyText = bodyComp ? bodyComp.text : '';

    try {
      setSendingTemplate(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/send-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          to: newChatPhone.trim(),
          templateName: newChatSelectedTemplate.name,
          languageCode: newChatSelectedTemplate.language,
          headerVariables: newChatHeaderVariables,
          bodyVariables: newChatBodyVariables,
          buttonVariables: newChatButtonVariables,
          templateBodyText,
          headerMediaUrl: newChatHeaderMediaUrl
        })
      });

      if (res.ok) {
        alert('Template message sent successfully!');
        onSuccess(newChatPhone.trim());
      } else {
        const data = await res.json();
        const detailMsg = data.details?.error?.message || (typeof data.details === 'string' ? data.details : JSON.stringify(data.details));
        alert(`${data.message || 'Failed to send template message.'}\n\nDetails: ${detailMsg}`);
      }
    } catch (err) {
      console.error('Error sending template message:', err);
      alert('Error sending template message. Please try again.');
    } finally {
      setSendingTemplate(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
        padding: '28px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PlusCircle size={20} color="#22c55e" />
            {initialPhone ? 'Send Template' : 'Initiate New Chat'}
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '18px' }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
          {!initialPhone && (
            <button
              onClick={() => setSendType('text')}
              style={{
                flex: 1, padding: '8px', borderRadius: '6px', cursor: 'pointer',
                background: sendType === 'text' ? '#e2e8f0' : 'transparent',
                border: '1px solid #cbd5e1', fontWeight: '600', color: '#334155'
              }}
            >
              Text Message
            </button>
          )}
          <button
            onClick={() => setSendType('template')}
            style={{
              flex: 1, padding: '8px', borderRadius: '6px', cursor: 'pointer',
              background: sendType === 'template' ? '#e2e8f0' : 'transparent',
              border: '1px solid #cbd5e1', fontWeight: '600', color: '#334155'
            }}
          >
            Template Message
          </button>
        </div>

        <form onSubmit={sendType === 'template' ? handleSendTemplate : handleSendText} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
              Recipient Phone Number (with Country Code, e.g. 919876543210)
            </label>
            <input
              type="text"
              placeholder="e.g. 919876543210"
              value={newChatPhone}
              onChange={e => setNewChatPhone(e.target.value)}
              required
              disabled={!!initialPhone}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', background: initialPhone ? '#f1f5f9' : 'white' }}
            />
          </div>

          {sendType === 'text' ? (
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Message
              </label>
              <textarea
                value={textMessage}
                onChange={e => setTextMessage(e.target.value)}
                placeholder="Type your message here..."
                required
                rows={4}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', resize: 'vertical' }}
              />
            </div>
          ) : (
            <>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Select Approved Template
                </label>
                {loadingTemplates ? (
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Loading templates...</div>
                ) : templates.filter(t => t.status === 'APPROVED').length === 0 ? (
                  <div style={{ fontSize: '12px', color: '#94a3b8', padding: '8px', border: '1px dashed #cbd5e1', borderRadius: '6px' }}>
                    No approved templates found.
                  </div>
                ) : (
                  <select
                    value={newChatSelectedTemplate?.name || ''}
                    onChange={(e) => {
                      const selected = templates.find(t => t.name === e.target.value);
                      setNewChatSelectedTemplate(selected);
                      setNewChatHeaderVariables({});
                      setNewChatHeaderMediaUrl('');
                      setNewChatBodyVariables({});
                      setNewChatButtonVariables({});
                    }}
                    required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', background: 'white', cursor: 'pointer' }}
                  >
                    <option value="" disabled>Select a template...</option>
                    {templates.filter(t => t.status === 'APPROVED').map((tmpl) => (
                      <option key={tmpl.name} value={tmpl.name}>
                        {tmpl.name.replace(/_/g, ' ')} ({tmpl.language})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {newChatSelectedTemplate && (
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Template Preview:</div>
                  <div style={{ fontSize: '13px', color: '#334155', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'HEADER')?.text && (
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'HEADER')?.text}
                      </div>
                    )}
                    {newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'BODY')?.text}
                  </div>
                </div>
              )}

              {newChatSelectedTemplate && ['IMAGE', 'DOCUMENT', 'VIDEO'].includes(newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'HEADER')?.format?.toUpperCase()) && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                    Header Media URL
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                      Provide URL for the {newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'HEADER')?.format?.toUpperCase()}
                    </label>
                    <input
                      type="url"
                      placeholder={`https://example.com/media`}
                      value={newChatHeaderMediaUrl}
                      onChange={(e) => setNewChatHeaderMediaUrl(e.target.value)}
                      required
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}
                    />
                  </div>
                </div>
              )}

              {newChatSelectedTemplate && getHeaderPlaceholders(newChatSelectedTemplate).length > 0 && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                    Header Variables
                  </div>
                  {getHeaderPlaceholders(newChatSelectedTemplate).map((num) => (
                    <div key={`header-${num}`} style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                        Header Variable {`{{${num}}}`}
                      </label>
                      <input
                        type="text"
                        placeholder={`Value for header {{${num}}}`}
                        value={newChatHeaderVariables[num] || ''}
                        onChange={(e) => {
                          setNewChatHeaderVariables(prev => ({
                            ...prev,
                            [num]: e.target.value
                          }));
                        }}
                        required
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {newChatSelectedTemplate && getBodyPlaceholders(newChatSelectedTemplate).length > 0 && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px', marginTop: '12px' }}>
                    Body Variables
                  </div>
                  {getBodyPlaceholders(newChatSelectedTemplate).map((num) => (
                    <div key={`body-${num}`} style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                        Body Variable {`{{${num}}}`}
                      </label>
                      <input
                        type="text"
                        placeholder={`Value for body {{${num}}}`}
                        value={newChatBodyVariables[num] || ''}
                        onChange={(e) => {
                          setNewChatBodyVariables(prev => ({
                            ...prev,
                            [num]: e.target.value
                          }));
                        }}
                        required
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {newChatSelectedTemplate && getTemplateButtonPlaceholders(newChatSelectedTemplate).length > 0 && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px', marginTop: '12px' }}>
                    Button URL Suffix Variables
                  </div>
                  {getTemplateButtonPlaceholders(newChatSelectedTemplate).map((btn) => (
                    <div key={btn.index} style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                        Button "{btn.text}" URL Variable
                      </label>
                      <input
                        type="text"
                        placeholder={`Suffix for ${btn.url}`}
                        value={newChatButtonVariables[btn.index] || ''}
                        onChange={(e) => {
                          setNewChatButtonVariables(prev => ({
                            ...prev,
                            [btn.index]: e.target.value
                          }));
                        }}
                        required
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              type="submit"
              disabled={sendingTemplate || (sendType === 'template' && !newChatSelectedTemplate)}
              style={{
                flex: 1,
                background: '#22c55e',
                border: 'none',
                color: 'white',
                padding: '12px',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(34,197,94,0.2)',
                opacity: (sendingTemplate || (sendType === 'template' && !newChatSelectedTemplate)) ? 0.6 : 1
              }}
            >
              {sendingTemplate ? 'Sending...' : 'Send'}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: 'white',
                fontWeight: '600',
                color: '#475569',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewChatModal;
